import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { isSigningOut } from "@/lib/sign-out";

import appCss from "../styles.css?url";
import faviconAsset from "@/assets/wc-favicon.png.asset.json";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { appleSplashLinks } from "@/lib/apple-splash-links";
import { AuthProvider } from "@/lib/auth-context";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { registerServiceWorker } from "@/lib/register-sw";
import { installClientErrorCapture } from "@/lib/client-error-capture";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try refreshing.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Wealthy Creatives CEO Boardroom" },
      { name: "description", content: "The CEO command center for creative entrepreneurs." },
      { name: "theme-color", content: "#0F0F0F" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Boardroom" },
      { property: "og:title", content: "Wealthy Creatives CEO Boardroom" },
      { name: "twitter:title", content: "Wealthy Creatives CEO Boardroom" },
      { property: "og:description", content: "The CEO command center for creative entrepreneurs." },
      { name: "twitter:description", content: "The CEO command center for creative entrepreneurs." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/1fbdbafe-de65-499d-8cc4-acb236f80f4c" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/1fbdbafe-de65-499d-8cc4-acb236f80f4c" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: faviconAsset.url },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "128x128", href: "/icon-128.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "196x196", href: "/icon-196.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png?v=2" },
      { rel: "apple-touch-icon", sizes: "57x57", href: "/apple-touch-icon-57.png?v=2" },
      { rel: "apple-touch-icon", sizes: "60x60", href: "/apple-touch-icon-60.png?v=2" },
      { rel: "apple-touch-icon", sizes: "72x72", href: "/apple-touch-icon-72.png?v=2" },
      { rel: "apple-touch-icon", sizes: "76x76", href: "/apple-touch-icon-76.png?v=2" },
      { rel: "apple-touch-icon", sizes: "114x114", href: "/apple-touch-icon-114.png?v=2" },
      { rel: "apple-touch-icon", sizes: "120x120", href: "/apple-touch-icon-120.png?v=2" },
      { rel: "apple-touch-icon", sizes: "144x144", href: "/apple-touch-icon-144.png?v=2" },
      { rel: "apple-touch-icon", sizes: "152x152", href: "/apple-touch-icon-152.png?v=2" },
      { rel: "apple-touch-icon", sizes: "167x167", href: "/apple-touch-icon-167.png?v=2" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon-180.png?v=2" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=2" },
      // Legacy iOS 6 and earlier — precomposed variants
      { rel: "apple-touch-icon-precomposed", sizes: "57x57", href: "/apple-touch-icon-57.png?v=2" },
      { rel: "apple-touch-icon-precomposed", sizes: "72x72", href: "/apple-touch-icon-72.png?v=2" },
      { rel: "apple-touch-icon-precomposed", sizes: "114x114", href: "/apple-touch-icon-114.png?v=2" },
      { rel: "apple-touch-icon-precomposed", sizes: "144x144", href: "/apple-touch-icon-144.png?v=2" },
      { rel: "apple-touch-icon-precomposed", href: "/apple-touch-icon.png?v=2" },
      { rel: "mask-icon", href: "/mask-icon.svg", color: "#D4AF37" },
      { rel: "manifest", href: "/manifest.webmanifest?v=2" },


      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@500;600;700&display=swap",
      },
      ...appleSplashLinks,
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthInvalidator() {
  const router = useRouter();
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (isSigningOut()) return;
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        router.invalidate();
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [router]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    installClientErrorCapture();
    registerServiceWorker();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthInvalidator />
        <Outlet />
        <Toaster theme="dark" position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}
